import { DOCUMENT, DestroyRef, Injectable, Signal, computed, inject, signal } from '@angular/core';

import {
  PaletteColor,
  StyleRuleLike,
  THEME_METADATA_VARS,
  ThemeInfo,
  ThemeMode,
  ThemeVariable,
  extractPalette,
  extractThemeVariables,
  extractThemes,
} from './theme-introspection.util';

/**
 * Reads the live compiled stylesheet to discover the color palette, available
 * themes, and each theme's CSS custom properties.
 *
 * Everything the theme playground needs already exists in the bundled CSS
 * (`.bg-color-*` palette classes, plus the shared light/dark base rules and each
 * theme's delta rule over them), so reading it at runtime keeps the playground
 * automatically in sync with SCSS — new palette colors, new theme variables, and
 * new themes are picked up with zero TypeScript changes. A theme's variables are
 * *composed* from its base + delta; see `extractThemeVariables`.
 *
 * The theme rules live in the app's global `styles.css`, which production builds
 * serve as a render-blocking `<link>` that is fetched + parsed asynchronously.
 * A read taken before that sheet's `cssRules` are populated sees only whatever
 * other (e.g. component) stylesheets have been injected — none of which carry the
 * theme markers. To avoid freezing such a partial view, discovery is **not
 * memoized until `document.readyState === 'complete'`** (the `load` milestone,
 * which waits for the parser-inserted global stylesheet), and the reactive
 * {@link themes}/{@link palette} signals recompute once that milestone is reached.
 */
@Injectable({ providedIn: 'root' })
export class ThemeIntrospectionService {
  private readonly document = inject(DOCUMENT);
  private readonly destroyRef = inject(DestroyRef);

  private cachedRules: StyleRuleLike[] | null = null;
  private cachedThemeRules: StyleRuleLike[] | null = null;

  /**
   * Bumped once the document finishes loading, so the reactive
   * {@link themes}/{@link palette} signals recompute against the now-complete
   * stylesheet set. See the constructor.
   */
  private readonly revision = signal(0);

  /**
   * Reactive view of the discovered themes. Unlike the imperative
   * {@link getThemes}, this recomputes once the stylesheet set is complete, so UI
   * that reads it never freezes an empty/partial pre-load snapshot — the bug
   * behind intermittently-missing theme cards on slower (CI) machines.
   */
  public readonly themes: Signal<ThemeInfo[]> = computed(() => {
    this.revision();
    return extractThemes(this.getThemeMetadataRules());
  });

  /** Reactive view of the palette; recomputes on load like {@link themes}. */
  public readonly palette: Signal<PaletteColor[]> = computed(() => {
    this.revision();
    return extractPalette(this.getStyleRules());
  });

  public constructor() {
    // If the document is still loading, the global theme stylesheet may not be
    // parsed yet. Recompute the reactive signals once `load` fires (by which
    // point the render-blocking `styles.css` is guaranteed parsed), so any UI
    // built on them picks up the themes without needing to be reopened.
    if (this.document.readyState !== 'complete') {
      const view = this.document.defaultView;
      if (view) {
        const onLoad = (): void => this.revision.update(value => value + 1);
        view.addEventListener('load', onLoad, { once: true });
        this.destroyRef.onDestroy(() => view.removeEventListener('load', onLoad));
      }
    }
  }

  /** Palette colors discovered from `.bg-color-<NAME>` rules. */
  public getPalette(): PaletteColor[] {
    return extractPalette(this.getStyleRules());
  }

  /** Themes discovered from bare single-class rules carrying `--theme-label`. */
  public getThemes(): ThemeInfo[] {
    return extractThemes(this.getThemeMetadataRules());
  }

  /**
   * Resolve a (family, mode) pair to the concrete theme class id whose
   * `--theme-family`/`--theme-mode` markers match, or `undefined` when no such
   * theme is discovered (e.g. before the stylesheet is parsed).
   */
  public resolveThemeId(family: string, mode: ThemeMode): string | undefined {
    return this.getThemes().find(theme => theme.family === family && theme.mode === mode)?.id;
  }

  /** Every editable CSS custom property + value for the given theme id. */
  public getThemeVariables(themeId: string): ThemeVariable[] {
    return extractThemeVariables(this.getStyleRules(), themeId);
  }

  /**
   * A metadata-only view of the stylesheet: the same {@link StyleRuleLike} shape
   * as {@link getStyleRules}, but each rule carries only the handful of properties
   * {@link extractThemes} actually reads.
   *
   * `ThemeService` resolves the body theme class during app bootstrap, so theme
   * discovery runs for every user of every app (spaces-ui, the embed,
   * meadow-docs). The full mirror allocates one object per declaration of every
   * rule in the document — thousands — purely so the admin-only playground can
   * enumerate variables. This reads a fixed five properties per rule and allocates
   * only for the ~18 rules that carry any of them.
   *
   * Same memoization rule as {@link getStyleRules}: never cache a pre-`complete`
   * read, or a partial view would be frozen in for the session.
   */
  private getThemeMetadataRules(): StyleRuleLike[] {
    if (this.cachedThemeRules) {
      return this.cachedThemeRules;
    }

    const rules: StyleRuleLike[] = [];
    this.forEachStyleRule(rule => {
      const declarations: Array<{ name: string; value: string }> = [];
      for (const name of THEME_METADATA_VARS) {
        const value = rule.style.getPropertyValue(name);
        if (value) {
          declarations.push({ name, value });
        }
      }
      if (declarations.length > 0) {
        rules.push({ selectorText: rule.selectorText, declarations });
      }
    });

    if (rules.length > 0 && this.document.readyState === 'complete') {
      this.cachedThemeRules = rules;
    }
    return rules;
  }

  /**
   * Flatten every accessible `CSSStyleRule` in the document into the testable
   * {@link StyleRuleLike} shape. Cross-origin sheets throw on `cssRules` access
   * and are skipped.
   *
   * Memoized only once the document has finished loading: until then the global
   * theme stylesheet may not be parsed, and caching that partial view would
   * permanently hide every theme. Before `complete`, re-read on every call so a
   * late read still sees the themes once `styles.css` lands.
   */
  private getStyleRules(): StyleRuleLike[] {
    if (this.cachedRules) {
      return this.cachedRules;
    }

    const rules: StyleRuleLike[] = [];
    this.forEachStyleRule(rule => {
      rules.push({
        selectorText: rule.selectorText,
        declarations: this.readDeclarations(rule.style),
      });
    });

    // Cache only once the stylesheet set is final (document fully loaded) and
    // actually yielded rules. A partial/empty pre-load read is returned but not
    // memoized, so the next read re-reads and finds the late-parsed theme rules.
    //
    // A partially-parsed sheet can't yield a theme delta without its base layer:
    // `_reset.scss` emits each base rule ahead of every delta that layers on it,
    // so rules arriving in source order always bring the base first.
    if (rules.length > 0 && this.document.readyState === 'complete') {
      this.cachedRules = rules;
    }
    return rules;
  }

  /** Visit every accessible top-level `CSSStyleRule`, skipping cross-origin sheets. */
  private forEachStyleRule(visit: (rule: CSSStyleRule) => void): void {
    const sheets = this.document.styleSheets;

    for (let i = 0; i < sheets.length; i++) {
      let cssRules: CSSRuleList;
      try {
        cssRules = sheets[i].cssRules;
      } catch {
        // Cross-origin stylesheet — not readable, skip it.
        continue;
      }

      for (let j = 0; j < cssRules.length; j++) {
        const rule = cssRules[j];
        if (!this.isStyleRule(rule)) {
          continue;
        }
        visit(rule);
      }
    }
  }

  private isStyleRule(rule: CSSRule): rule is CSSStyleRule {
    // CSSRule.STYLE_RULE === 1; guard via the `selectorText` property so this
    // works even when the CSSRule type constants are unavailable.
    return 'selectorText' in rule && 'style' in rule;
  }

  private readDeclarations(style: CSSStyleDeclaration): Array<{ name: string; value: string }> {
    const declarations: Array<{ name: string; value: string }> = [];
    for (let i = 0; i < style.length; i++) {
      const name = style[i];
      declarations.push({ name, value: style.getPropertyValue(name) });
    }
    return declarations;
  }
}
